import dayjs from 'dayjs';

import { HttpHeader, HttpResponse, SuccessStatusCode } from '../@types';

// eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
interface SonarFetchBody extends Object {
	file?: File;
	fileOptions?: { formName: string; file: File };
	formFields?: Record<string, string>;
	multipart?: boolean;
}

interface SonarFetchOptions {
	body?: SonarFetchBody;
	endpoint: string;
	headers?: Record<string, string | undefined>;
	method: string;
	signal?: AbortSignal;
	subdomain?: string;
	skipAuthRefresh?: boolean;
	skipSonarHeaders?: boolean;
}

const authTokenLocalStoragKey = 'authToken';

function getAuthToken(): string | undefined {
	const authTokenStr = localStorage.getItem(authTokenLocalStoragKey);

	if (!authTokenStr) {
		return;
	}

	const authTokenObj = JSON.parse(authTokenStr);
	const { token } = authTokenObj;

	return token;
}

function setAuthToken(token: string): void {
	localStorage.setItem(authTokenLocalStoragKey, JSON.stringify({ token }));
}

function withAuthHeader(authToken: string | undefined): Record<string, string> | null {
	if (authToken) {
		return {
			[ HttpHeader.Authorization ]: `Bearer ${authToken}`
		};
	}

	return null;
}

function withAuthRefreshHeader(authToken: string | undefined, skip?: boolean): Record<string, string> | null {
	if (authToken && !skip) {
		return {
			[ HttpHeader.RefreshToken ]: 'true'
		};
	}

	return null;
}

function withSubdomainHeader(subdomain: string | null | undefined): Record<string, string> | null {
	if (subdomain) {
		return {
			[ HttpHeader.Subdomain ]: subdomain
		};
	}

	return null;
}

function withTimezoneHeader(): Record<string, string> {
	return {
		[ HttpHeader.Timezone ]: dayjs()
			.utcOffset()
			.toString()
	};
}

function parseResponseHeaders(headers: Headers): void {
	if (headers.has(HttpHeader.Authorization)) {
		const value = (headers.get(HttpHeader.Authorization) ?? '').replace('Bearer ', '');

		if (value) {
			setAuthToken(value);
		}
	}
}

function sonarFetch(options: SonarFetchOptions): Promise<Response> {
	const { body, endpoint, headers, method, signal, skipAuthRefresh, skipSonarHeaders, subdomain } = options;

	const authToken = getAuthToken();

	const sonarHeaders = skipSonarHeaders ? {} : {
		...withAuthHeader(authToken),
		...withAuthRefreshHeader(authToken, skipAuthRefresh),
		...withSubdomainHeader(subdomain),
		...withTimezoneHeader()
	};

	const requestHeaders: HeadersInit = {
		Accept: 'application/json',
		...headers,
		...sonarHeaders
	};

	let fetchPromise: Promise<Response>;

	if (body) {
		const { file, fileOptions, formFields, multipart } = body;

		if (file || fileOptions || multipart) {
			const formData = new FormData();

			if (file) {
				formData.append('file', file);
			}

			if (fileOptions) {
				formData.append(fileOptions.formName, fileOptions.file);
			}

			if (formFields) {
				for (const [ key, value ] of Object.entries(formFields)) {
					formData.append(key, value);
				}
			}

			fetchPromise = fetch(endpoint, {
				body: formData,
				headers: requestHeaders,
				method,
				signal
			});
		} else {
			fetchPromise = fetch(endpoint, {
				body: JSON.stringify(body),
				headers: {
					...requestHeaders,
					[ HttpHeader.ContentType ]: 'application/json'
				},
				method,
				signal
			});
		}
	} else {
		fetchPromise = fetch(endpoint, {
			headers: requestHeaders,
			method,
			signal
		});
	}

	return fetchPromise.then((response) => {
		parseResponseHeaders(response.headers);

		return response;
	});
}

/*
	To support primitive strings returns from API endpoints, need to verify if data content is a JSON value
	or a primitive string value. JSON.parse(...) throws an exception attempting to parse non-json strings.
	https://github.com/github/fetch/issues/268#issuecomment-293522869
*/
function isJSON(input: string): boolean {
	const rxOne = /^[\],:{}\s]*$/u;
	const rxTwo = /\\(?:["\\/bfnrt]|u[0-9a-fA-F]{4})/gu;
	const rxThree = /"[^"\\\n\r]*"|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?/gu;
	const rxFour = /(?:^|:|,)(?:\s*\[)+/gu;

	return Boolean(input.length) && rxOne.test(input.replace(rxTwo, '@').replace(rxThree, ']')
		.replace(rxFour, ''));
}

async function getResponsePromiseWithData<T>(response: Response): Promise<HttpResponse<T>> {
	const { headers, status } = response;
	const result: HttpResponse<T> = {
		headers,
		response,
		status
	};

	if (status === SuccessStatusCode.Ok || status === SuccessStatusCode.Accepted) {
		const rawTextPromise = response.text() as Promise<string>;
		const rawText = await rawTextPromise;
		const data = isJSON(rawText) ? JSON.parse(rawText) : rawText;

		return {
			data: data as T,
			...result
		};
	}

	return Promise.resolve(result);
}

async function getResponsePromiseWithBlobData(response: Response): Promise<HttpResponse<Blob>> {
	const { headers, status } = response;
	const result: HttpResponse<Blob> = {
		headers,
		response,
		status
	};

	if (status === SuccessStatusCode.Ok) {
		const data = await response.blob();

		return {
			data,
			...result
		};
	}

	return Promise.resolve(result);
}

function getResponseWithStreamData(
	response: Response
): HttpResponse<ReadableStreamDefaultReader<Uint8Array<ArrayBufferLike>>> {
	const { headers, status } = response;
	const result: HttpResponse<ReadableStreamDefaultReader<Uint8Array<ArrayBufferLike>>> = {
		headers,
		response,
		status
	};

	if (status === SuccessStatusCode.Ok) {
		const data = response.body!.getReader();

		return {
			data,
			...result
		};
	}

	return result;
}

export interface SonarHttpOptions<TRequest = void> {
	headers?: Record<string, string | undefined>;
	requestBody?: TRequest;
	signal?: AbortSignal;
	subdomain?: string;
	targetUri: string;
	skipAuthRefresh?: boolean;
	skipSonarHeaders?: boolean;
	uriType?: 'relative' | 'absolute';
}

function getFetchOptions<TRequest = void>(options: SonarHttpOptions<TRequest>): SonarFetchOptions {
	const api = localStorage.getItem('api');

	const { headers, requestBody, signal, skipAuthRefresh, skipSonarHeaders, subdomain, targetUri, uriType } = options;

	const endpoint = uriType === 'absolute' ? targetUri : `${api}${targetUri}`;

	return {
		body: requestBody as SonarFetchBody,
		endpoint,
		headers,
		method: 'GET',
		signal,
		skipAuthRefresh,
		skipSonarHeaders,
		subdomain
	};
}

export async function sonarHttpDelete<TRequest = void, TResponse = void>(
	options: SonarHttpOptions<TRequest>
): Promise<HttpResponse<TResponse>> {
	const response = await sonarFetch({
		...getFetchOptions(options),
		method: 'DELETE'
	});

	return getResponsePromiseWithData(response);
}

export async function sonarHttpGet<TRequest = void, TResponse = void>(
	options: SonarHttpOptions<TRequest>
): Promise<HttpResponse<TResponse>> {
	const response = await sonarFetch({
		...getFetchOptions(options),
		body: undefined,
		method: 'GET'
	});

	return getResponsePromiseWithData(response);
}

export async function sonarHttpGetBlob<TRequest = void>(
	options: SonarHttpOptions<TRequest>
): Promise<HttpResponse<Blob>> {
	const response = await sonarFetch({
		...getFetchOptions(options),
		body: undefined,
		method: 'GET'
	});

	return getResponsePromiseWithBlobData(response);
}

export async function sonarHttpGetStream<TRequest = void>(
	options: SonarHttpOptions<TRequest>
): Promise<HttpResponse<ReadableStreamDefaultReader<Uint8Array<ArrayBufferLike>>>> {
	const response = await sonarFetch({
		...getFetchOptions(options),
		body: undefined,
		headers: {
			[ HttpHeader.Accept ]: 'text/event-stream'
		},
		method: 'GET'
	});

	return getResponseWithStreamData(response);
}

export async function sonarHttpPostStream<TRequest = void>(
	options: SonarHttpOptions<TRequest>
): Promise<HttpResponse<ReadableStreamDefaultReader<Uint8Array<ArrayBufferLike>>>> {
	const response = await sonarFetch({
		...getFetchOptions(options),
		headers: {
			...options.headers,
			[ HttpHeader.Accept ]: 'text/event-stream'
		},
		method: 'POST'
	});

	return getResponseWithStreamData(response);
}

export async function sonarHttpPatch<TRequest = void, TResponse = void>(
	options: SonarHttpOptions<TRequest>
): Promise<HttpResponse<TResponse>> {
	const response = await sonarFetch({
		...getFetchOptions(options),
		method: 'PATCH'
	});

	return getResponsePromiseWithData(response);
}

export async function sonarHttpPost<TRequest = void, TResponse = void>(
	options: SonarHttpOptions<TRequest>
): Promise<HttpResponse<TResponse>> {
	const response = await sonarFetch({
		...getFetchOptions(options),
		method: 'POST'
	});

	return getResponsePromiseWithData(response);
}

export async function sonarHttpPut<TRequest = void, TResponse = void>(
	options: SonarHttpOptions<TRequest>
): Promise<HttpResponse<TResponse>> {
	const response = await sonarFetch({
		...getFetchOptions(options),
		method: 'PUT'
	});

	return getResponsePromiseWithData(response);
}
